import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  ColorPicker,
  createListCollection,
  Heading,
  HStack,
  IconButton,
  Image,
  parseColor,
  Popover,
  Portal,
  Select,
  SimpleGrid,
  Slider,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react";
import { LuGlobe, LuGrid2X2, LuMap, LuPlus, LuRefreshCcw } from "react-icons/lu";
import { rgbaToHex, saveToCache, showOrHideTimeLine, showToast } from "../../../utilies/Utility";

import { setImageryByName } from "../../../utilies/ImageryUtility";
import Type from "../../../utilies/Types";
import { addGrid, removeGrid } from "../../../utilies/GridUtility";
import { serviceGetImagary } from "../../../api/services/settings";
import { setSceneMode } from "../../../utilies/MapUtility";
import { useAppStore } from "../../../context/store";
import { Tooltip } from "../../ui/tooltip";
import { loadAerialImageryTypeAsync } from "../../../utilies/loader/LoadImagery";
import { useMapObjects } from "../../../context/MapObjectsContext";

function MapSettings() {
  // state
  const showMapInfo = useAppStore((s) => s.showMapInfo);
  const showCameraInfo = useAppStore((s) => s.showCameraInfo);
  const cesiumSetting = useAppStore((s) => s.cesiumSetting);
  const timeLineMode = useAppStore((s) => s.timeLineMode);
  const gridMode = useAppStore((s) => s.gridMode);
  const gridSize = useAppStore((s) => s.gridSize);
  const gridColor = useAppStore((s) => s.gridColor);
  const cesiumAssetsList = useAppStore((s) => s.cesiumAssetsList);
  const userData = useAppStore((s) => s.userData);
  const selectedProject = useAppStore((s) => s.selectedProject);

  // refs / setters / objects
  const setShowMapInfo = useAppStore.getState().setShowMapInfo;
  const setShowCameraInfo = useAppStore.getState().setShowCameraInfo;
  const viewerRef = useAppStore.getState().viewerRef;
  const setCesiumSetting = useAppStore.getState().setCesiumSetting;
  const setTimeLineMode = useAppStore.getState().setTimeLineMode;
  const setMapStyle = useAppStore.getState().setMapStyle;
  const setGridSize = useAppStore.getState().setGridSize;
  const setGridColor = useAppStore.getState().setGridColor;
  const setGridMode = useAppStore.getState().setGridMode;
  const setCesiumAssetsList = useAppStore.getState().setCesiumAssetsList;

  const { mapObjects, setMapObjects, mapObjectsRef } = useMapObjects();

  const [titleOptions, setTitleOptions] = useState(
    createListCollection({
      items: [],
    }),
  );
  useEffect(() => {
    // run only if list is not empty
    if (cesiumAssetsList && cesiumAssetsList.length > 0) {
      try {
        // create flat list of options
        const items = cesiumAssetsList.map((it) => ({
          label: it.name ?? "Unnamed", // show name if available
          value: it.url ?? it.cesium_asset_id ?? it.id,
        }));

        // sort alphabetically by label
        const sortedItems = items.sort((a, b) => a.label.localeCompare(b.label));

        // update state
        setTitleOptions(
          createListCollection({
            items: sortedItems,
          }),
        );
      } catch (e) {
        console.error("Error creating asset list:", e);
      }
    }
  }, [cesiumAssetsList]);

  const [loadingRefresh, setLoadingRefresh] = useState(false);
  const refreshAerialImagery = () => {
    setLoadingRefresh(true);
    serviceGetImagary(null, userData.token)
      .then((res) => {
        if (res?.data?.assets) {
          setCesiumAssetsList(res?.data?.assets);
          showToast("Updated", Type.NOTIFICATION.SUCCESS);
        }
        setLoadingRefresh(false);
      })
      .catch((err) => {
        console.error("Error", err);
        setLoadingRefresh(false);
      });
  };

  const [localAssetId, setLocalAssetId] = useState("");
  const changeMapView = (type) => {
    switch (type) {
      case Type.SCENE.SCENE_2D:
        setSceneMode(viewerRef.current, Type.SCENE.SCENE_2D);
        setCesiumSetting((prev) => ({
          ...prev,
          map_view: Type.SCENE.SCENE_2D,
        }));
        break;
      case Type.SCENE.SCENE_3D:
        setSceneMode(viewerRef.current, Type.SCENE.SCENE_3D);
        setCesiumSetting((prev) => ({
          ...prev,
          map_view: Type.SCENE.SCENE_3D,
        }));
        break;
      case Type.SCENE.SCENE_COLUMBUS:
        setSceneMode(viewerRef.current, Type.SCENE.SCENE_COLUMBUS);
        setCesiumSetting((prev) => ({
          ...prev,
          map_view: Type.SCENE.SCENE_COLUMBUS,
        }));
        break;
    }
  };

  return (
    <Popover.Root positioning={{ placement: "bottom-end" }}>
      <Popover.Trigger asChild>
        <IconButton userSelect={"none"} rounded="full" boxShadow="lg" bg={"Background"}>
          <Tooltip content="Map Settings">
            <LuMap color="MenuText" />
          </Tooltip>
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content width={"fit-content"} overflowX={"none"} pr={1}>
            <Popover.Arrow />
            <Popover.Body p={2} scrollBehavior={"inside"} maxHeight={"75vh"} overflowY={"auto"} overflowX={"none"} width={"full"}>
              <Box>
                <Heading fontSize={"sm"} fontWeight={"medium"}>
                  Map View
                </Heading>
                <HStack py={2}>
                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    boxShadow="lg"
                    bg={cesiumSetting.map_view == Type.SCENE.SCENE_2D ? "green" : "Background"}
                    onClick={() => {
                      changeMapView(Type.SCENE.SCENE_2D);
                    }}
                  >
                    <HStack gap={2} mx={2} align="center">
                      <LuGrid2X2 color={cesiumSetting.map_view == Type.SCENE.SCENE_2D ? "white" : "MenuText"} />
                      <Text textStyle="xs" color={cesiumSetting.map_view == Type.SCENE.SCENE_2D ? "white" : "MenuText"}>
                        2D
                      </Text>
                    </HStack>
                  </Button>
                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    boxShadow="lg"
                    bg={cesiumSetting.map_view == Type.SCENE.SCENE_COLUMBUS ? "green" : "Background"}
                    onClick={() => {
                      changeMapView(Type.SCENE.SCENE_COLUMBUS);
                    }}
                  >
                    <HStack gap={2} mx={2} align="center">
                      <LuGrid2X2 color={cesiumSetting.map_view == Type.SCENE.SCENE_COLUMBUS ? "white" : "MenuText"} />
                      <Text textStyle="xs" color={cesiumSetting.map_view == Type.SCENE.SCENE_COLUMBUS ? "white" : "MenuText"}>
                        2.5D
                      </Text>
                    </HStack>
                  </Button>
                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    boxShadow="lg"
                    bg={cesiumSetting.map_view == Type.SCENE.SCENE_3D ? "green" : "Background"}
                    onClick={() => {
                      changeMapView(Type.SCENE.SCENE_3D);
                    }}
                  >
                    <HStack gap={2} mx={2} align="center">
                      <LuGlobe color={cesiumSetting.map_view == Type.SCENE.SCENE_3D ? "white" : "MenuText"} />
                      <Text textStyle="xs" color={cesiumSetting.map_view == Type.SCENE.SCENE_3D ? "white" : "MenuText"}>
                        3D
                      </Text>
                    </HStack>
                  </Button>
                </HStack>
              </Box>
              <Box>
                <Heading fontSize={"sm"} fontWeight={"medium"}>
                  Base Map
                </Heading>
                <SimpleGrid columns={4} gap={1} py={2}>
                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Bing Maps Aerial");
                      setImageryByName(viewerRef.current, "Bing Maps Aerial");
                    }}
                  >
                    <VStack gap={0}>
                      <Image
                        userSelect={"none"}
                        width={"100px"}
                        height={"100px"}
                        rounded="lg"
                        objectFit={"cover"}
                        draggable={false}
                        src="/cesium/Widgets/Images/ImageryProviders/bingAerial.png"
                        alt="Logo"
                      />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Bing Maps Aerial
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Bing Maps Aerial with Labels");
                      setImageryByName(viewerRef.current, "Bing Maps Aerial with Labels");
                    }}
                  >
                    <VStack gap={0}>
                      <Image
                        userSelect={"none"}
                        width={"100px"}
                        height={"100px"}
                        rounded="lg"
                        objectFit={"cover"}
                        draggable={false}
                        src="/cesium/Widgets/Images/ImageryProviders/bingAerialLabels.png"
                        alt="Logo"
                      />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Bing Maps Aerial with Labels
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Bing Maps Roads");
                      setImageryByName(viewerRef.current, "Bing Maps Roads");
                    }}
                  >
                    <VStack gap={0}>
                      <Image
                        userSelect={"none"}
                        width={"100px"}
                        height={"100px"}
                        rounded="lg"
                        objectFit={"cover"}
                        draggable={false}
                        src="/cesium/Widgets/Images/ImageryProviders/bingRoads.png"
                        alt="Logo"
                      />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Bing Maps Roads
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("ArcGIS World Imagery");
                      setImageryByName(viewerRef.current, "ArcGIS World Imagery");
                    }}
                  >
                    <VStack gap={0}>
                      <Image
                        userSelect={"none"}
                        width={"100px"}
                        height={"100px"}
                        rounded="lg"
                        objectFit={"cover"}
                        draggable={false}
                        src="/cesium/Widgets/Images/ImageryProviders/ArcGisMapServiceWorldImagery.png"
                        alt="Logo"
                      />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        ArcGIS World Imagery
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("OpenStreetMap");
                      setImageryByName(viewerRef.current, "OpenStreetMap");
                    }}
                  >
                    <VStack gap={0}>
                      <Image
                        userSelect={"none"}
                        width={"100px"}
                        height={"100px"}
                        rounded="lg"
                        objectFit={"cover"}
                        draggable={false}
                        src="/cesium/Widgets/Images/ImageryProviders/openStreetMap.png"
                        alt="Logo"
                      />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        openStreetMap
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Mapbox Streets");
                      setImageryByName(viewerRef.current, "Mapbox Streets");
                    }}
                  >
                    <VStack gap={0}>
                      <Image userSelect={"none"} width={"100px"} height={"100px"} rounded="lg" objectFit={"cover"} draggable={false} src="/assets/image/imagery/MapboxStreets.png" alt="Logo" />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Mapbox Streets
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Mapbox Light");
                      setImageryByName(viewerRef.current, "Mapbox Light");
                    }}
                  >
                    <VStack gap={0}>
                      <Image userSelect={"none"} width={"100px"} height={"100px"} rounded="lg" objectFit={"cover"} draggable={false} src="/assets/image/imagery/MapboxLight.png" alt="Logo" />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Mapbox Light
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Mapbox Dark");
                      setImageryByName(viewerRef.current, "Mapbox Dark");
                    }}
                  >
                    <VStack gap={0}>
                      <Image userSelect={"none"} width={"100px"} height={"100px"} rounded="lg" objectFit={"cover"} draggable={false} src="/assets/image/imagery/MapboxDark.jpg" alt="Logo" />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Mapbox Dark
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Gray");
                      setImageryByName(viewerRef.current, "Gray");
                    }}
                  >
                    <VStack gap={0}>
                      <Box bg={"#646464CC"} width={"100px"} height={"100px"} userSelect={"none"} rounded="lg" />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Gray
                      </Text>
                    </VStack>
                  </Button>

                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("White");
                      setImageryByName(viewerRef.current, "White");
                    }}
                  >
                    <VStack gap={0}>
                      <Box bg={"#f5fffa"} width={"100px"} height={"100px"} userSelect={"none"} rounded="lg" />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        White
                      </Text>
                    </VStack>
                  </Button>
                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Color:#000000");
                      setImageryByName(viewerRef.current, "Color", "#000000");
                    }}
                  >
                    <VStack gap={0}>
                      <Box bg={"#000000"} width={"100px"} height={"100px"} userSelect={"none"} rounded="lg" />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Black
                      </Text>
                    </VStack>
                  </Button>
                  <Button
                    userSelect={"none"}
                    rounded="lg"
                    bg={"transparent"}
                    p={0}
                    height={"120px"}
                    onClick={() => {
                      setMapStyle("Color:#182bd3ff");
                      setImageryByName(viewerRef.current, "Color", "#182bd3ff");
                    }}
                  >
                    <VStack gap={0}>
                      <Box bg={"#182bd3ff"} width={"100px"} height={"100px"} userSelect={"none"} rounded="lg" />
                      <Text color={"MenuText"} mt={1} lineClamp={1} fontSize={"x-small"} lineHeight={"13px"}>
                        Blue
                      </Text>
                    </VStack>
                  </Button>
                </SimpleGrid>
              </Box>

              <Box mt={3}>
                <Heading fontSize={"sm"} fontWeight={"medium"}>
                  Aerial Imagery
                </Heading>
                <HStack gap={2}>
                  <Select.Root
                    collection={titleOptions}
                    size="sm"
                    width="full"
                    onValueChange={(v) => {
                      setLocalAssetId(v.value[0] ? v.value[0] : 0);
                    }}
                  >
                    <Select.HiddenSelect />

                    <Select.Control>
                      <Select.Trigger>
                        <Select.ValueText placeholder={"Select Asset"} />
                      </Select.Trigger>
                      <Select.IndicatorGroup>
                        <Select.ClearTrigger />
                        <Select.Indicator />
                      </Select.IndicatorGroup>
                    </Select.Control>

                    <Select.Positioner>
                      <Select.Content>
                        {titleOptions.items.map((item) => (
                          <Select.Item item={item} key={item.value}>
                            {item.label}
                            <Select.ItemIndicator />
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Positioner>
                  </Select.Root>
                  <Button
                    px={0}
                    disabled={selectedProject && localAssetId ? false : true}
                    onClick={() => {
                      loadAerialImageryTypeAsync({
                        viewerRef,
                        setMapObjects,
                        idOrUrl: localAssetId,
                        // idOrUrl: 3830183,
                        fileName: "Imagery",
                        savedStyles: { style_invisible_default: false, style_alpha: 1 },
                        projectId: selectedProject?.id,
                        projectTypeLoading: "main",
                      });
                    }}
                  >
                    <Tooltip content={"Add To Project"}>
                      <LuPlus />
                    </Tooltip>
                  </Button>
                  <Button
                    onClick={() => {
                      setImageryByName(viewerRef.current, "GeoTiff Tile", localAssetId);
                    }}
                  >
                    Load
                  </Button>
                  <Button onClick={refreshAerialImagery}>{loadingRefresh ? <Spinner /> : <LuRefreshCcw />}</Button>
                </HStack>
              </Box>

              <Box mt={3}>
                <Heading fontSize={"sm"} fontWeight={"medium"}>
                  Solid Background
                </Heading>
                <SimpleGrid columns={1} gap={1} py={2}>
                  <ColorPicker.Root
                    defaultValue={parseColor("#ff00ff")}
                    width={"full"}
                    onValueChange={(v) => {
                      setMapStyle("Color:" + rgbaToHex(v.valueAsString));
                      setImageryByName(viewerRef.current, "Color", rgbaToHex(v.valueAsString));
                    }}
                  >
                    <ColorPicker.HiddenInput />
                    <ColorPicker.Control>
                      <ColorPicker.Input />
                      <ColorPicker.Trigger />
                    </ColorPicker.Control>

                    <ColorPicker.Positioner>
                      <ColorPicker.Content>
                        <ColorPicker.Area />
                        <HStack>
                          <ColorPicker.EyeDropper size="xs" variant="outline" />
                          <ColorPicker.Sliders />
                        </HStack>
                      </ColorPicker.Content>
                    </ColorPicker.Positioner>
                  </ColorPicker.Root>
                </SimpleGrid>
              </Box>

              <HStack mt={0} gap={4}>
                <Box>
                  <Heading fontSize={"sm"} fontWeight={"medium"}>
                    Timeline
                  </Heading>
                  <Checkbox.Root
                    checked={timeLineMode}
                    onCheckedChange={(e) => {
                      setTimeLineMode(!!e.checked);
                      showOrHideTimeLine(viewerRef.current, !!e.checked);
                      saveToCache(Type.STORAGE.STATE_TIMELINE, !!e.checked);
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label fontSize={"xs"}>Always Show</Checkbox.Label>
                  </Checkbox.Root>
                </Box>
                <Box>
                  <Heading fontSize={"sm"} fontWeight={"medium"}>
                    Map Info
                  </Heading>
                  <Checkbox.Root
                    checked={showMapInfo}
                    onCheckedChange={(e) => {
                      setShowMapInfo(!!e.checked);
                      saveToCache("setting_mapinfo", !!e.checked);
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label fontSize={"xs"}>Always Show</Checkbox.Label>
                  </Checkbox.Root>
                </Box>
                <Box>
                  <Heading fontSize={"sm"} fontWeight={"medium"}>
                    Camera Info
                  </Heading>
                  <Checkbox.Root
                    checked={showCameraInfo}
                    onCheckedChange={(e) => {
                      setShowCameraInfo(!!e.checked);
                      saveToCache("setting_camerainfo", !!e.checked);
                    }}
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label fontSize={"xs"}>Always Show</Checkbox.Label>
                  </Checkbox.Root>
                </Box>
              </HStack>

              <Box mt={2}>
                <Heading fontSize={"sm"} fontWeight={"medium"}>
                  Grid
                </Heading>
                <Checkbox.Root
                  checked={gridMode}
                  onCheckedChange={(e) => {
                    setGridMode(!!e.checked);
                    if (!!e.checked) {
                      addGrid(viewerRef.current, gridSize[0], gridColor);
                      saveToCache(Type.STORAGE.STATE_GRID, {
                        visibility: true,
                        color: gridColor,
                        size: gridSize[0],
                      });
                    } else {
                      removeGrid(viewerRef.current);
                      saveToCache(Type.STORAGE.STATE_GRID, {
                        visibility: false,
                        color: gridColor,
                        size: gridSize[0],
                      });
                    }
                  }}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label fontSize={"xs"}>Show</Checkbox.Label>
                </Checkbox.Root>
                <HStack width={"full"}>
                  <Slider.Root
                    width={"full"}
                    size="sm"
                    defaultValue={gridSize}
                    min={10}
                    max={1614}
                    onChange={(value) => {
                      setGridSize([Number(value.target.value)]);

                      if (gridMode) {
                        removeGrid(viewerRef.current);
                        addGrid(viewerRef.current, Number(value.target.value), gridColor);
                      }

                      saveToCache(Type.STORAGE.STATE_GRID, {
                        visibility: gridMode,
                        color: gridColor,
                        size: Number(value.target.value),
                      });
                    }}
                  >
                    <HStack justify="space-between" mt={0}>
                      <Slider.Label fontSize={"x-small"}>Grid Size (m)</Slider.Label>
                      <Slider.ValueText fontSize={"x-small"} />
                    </HStack>
                    <Slider.Control>
                      <Slider.Track>
                        <Slider.Range />
                      </Slider.Track>
                      <Slider.Thumbs />
                    </Slider.Control>
                  </Slider.Root>
                  <ColorPicker.Root
                    value={parseColor(gridColor)}
                    onValueChange={(v) => {
                      setGridColor(rgbaToHex(v.valueAsString));
                      if (gridMode) {
                        removeGrid(viewerRef.current);
                        addGrid(viewerRef.current, gridSize[0], rgbaToHex(v.valueAsString));
                      }

                      saveToCache(Type.STORAGE.STATE_GRID, {
                        visibility: true,
                        color: rgbaToHex(v.valueAsString),
                        size: gridSize[0],
                      });
                    }}
                  >
                    <ColorPicker.HiddenInput />

                    <ColorPicker.Control>
                      <ColorPicker.Trigger />
                    </ColorPicker.Control>

                    <ColorPicker.Positioner>
                      <ColorPicker.Content>
                        <ColorPicker.Area />
                        <HStack>
                          <ColorPicker.EyeDropper size="xs" variant="outline" />
                          <ColorPicker.Sliders />
                        </HStack>
                      </ColorPicker.Content>
                    </ColorPicker.Positioner>
                  </ColorPicker.Root>
                </HStack>
              </Box>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export default MapSettings;
